import React, { useState } from 'react';
import { Pressable, View } from 'react-native';
import dayjs from 'dayjs';
import { useNavigation } from '@react-navigation/native';
import { Icon, Table } from '../index';
import {
  AdminPostRead,
  GetAdminHelpdeskIssuesParams,
  HelpdeskIssueStatus,
  PersonaType,
} from '../../client/interfaces';
import { useMessage } from '../../contexts/Messages/MessageContext';
import svgIcons from '../../assets';
import Modal from '../Modal';
import TableCell from '../TableCell';
import client from '../../client/client';
import { AppNavigationProp, ScreenName } from '../../navigation/types';
import { useAppTranslation } from '../../hooks/useAppTranslation';

interface Props {
  rows: AdminPostRead[];
  paramSetter: React.Dispatch<React.SetStateAction<GetAdminHelpdeskIssuesParams>>;
  params: GetAdminHelpdeskIssuesParams;
  refresh: () => Promise<void>;
  isLoading?: boolean;
}

const HelpdeskIssueTable: React.FC<Props> = ({
  rows,
  paramSetter,
  params,
  refresh,
  isLoading = false,
}) => {
  const { t } = useAppTranslation();
  const navigation = useNavigation<AppNavigationProp>();

  const { setMessage } = useMessage();

  const [issueToDelete, setIssueToDelete] = useState<AdminPostRead>();

  const deleteHelpdeskIssue = async (helpdeskIssue: AdminPostRead) => {
    try {
      await client.deletePost(helpdeskIssue.id);
      setMessage({
        message: t('success_messages:deleted'),
        type: 'success',
        translateMessage: false,
      });
      refresh();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setMessage({ message: e.message, type: 'error' });
    }
  };

  return (
    <View className="z-[10]">
      <Table
        paramSetter={paramSetter}
        params={params}
        isLoading={isLoading}
        keyFieldName="id"
        cellClassNamesFunction={(row, columnKey) =>
          columnKey !== 'actions' &&
          (row as AdminPostRead).helpdeskIssue?.status === HelpdeskIssueStatus.archived
            ? 'opacity-50'
            : ''
        }
        columns={[
          {
            key: 'title',
            label: t('admin:table_columns:helpdesk_issues.title'),
            sortable: true,
            widthPercentage: 15,
            templateFunction: (row) => <TableCell text={(row as AdminPostRead).titleTranslation} />,
          },
          {
            key: 'created_at_utc',
            widthPercentage: 12,
            label: t('admin:table_columns:common.created_at'),
            sortable: true,
            templateFunction: (row) => (
              <TableCell
                text={dayjs(row.createdAtUtc).format('YYYY. MM. DD.')}
                key={`created_at_utc-${row.id}`}
              />
            ),
          },
          {
            key: 'lastEditedBy',
            label: t('admin:table_columns:common.last_edited_by'),
            widthPercentage: 12,
            templateFunction: (row) => (
              <TableCell
                text={(row as AdminPostRead).lastEditedBy}
                key={(row as AdminPostRead).lastEditedBy}
              />
            ),
          },
          {
            key: 'topic_name',
            label: t('admin:table_columns:common.topics'),
            widthPercentage: 15,
            sortable: true,
            templateFunction: (row) => (
              <TableCell
                text={(row as AdminPostRead).helpdeskIssue?.topicName}
                key={`topic-${row.id}`}
              />
            ),
          },
          {
            key: 'organization_name',
            widthPercentage: 15,
            sortable: true,
            label: t('admin:table_columns:common.departments'),
            templateFunction: (row) => (
              <TableCell
                text={(row as AdminPostRead).helpdeskIssue?.organizationName}
                key={`org-${row.id}`}
              />
            ),
          },
          {
            key: 'personas',
            label: t('admin:table_columns:common.personas'),
            relationTransformer: (row) => {
              const issueRow = row as AdminPostRead;
              let relations = [t('personas:all')];
              if ((issueRow.personas?.length ?? 0) < Object.keys(PersonaType).length) {
                relations = issueRow.personas.map((rowPersona) =>
                  t(`personas:${rowPersona.key}`)?.toLowerCase(),
                );
              }

              return relations;
            },
          },
          {
            key: 'status',
            label: t('admin:table_columns:common.status'),
            widthPercentage: 10,
          },
          {
            key: 'actions',
            label: '',
            widthPercentage: 10,
            templateFunction: (row) => (
              <TableCell key={`actions-${row.id}`} classNames="flex-row justify-end">
                <Pressable
                  onPress={() => {
                    navigation.navigate(ScreenName.AdminHelpdeskIssueEditScreen, {
                      postId: row.id,
                      initialStatus: (row as AdminPostRead).helpdeskIssue?.status,
                    });
                  }}
                  className="items-center w-[24px] mr-4"
                >
                  <View className="cursor-pointer">
                    <Icon
                      icon={svgIcons.editIcon}
                      mobileSize={{ width: 24, height: 24 }}
                      webSize={{ width: 24, height: 24 }}
                    />
                  </View>
                </Pressable>
                {
                  <Pressable
                    onPress={() => {
                      setIssueToDelete(row as AdminPostRead);
                    }}
                    className="items-center w-[24px]"
                  >
                    <View className="cursor-pointer">
                      <Icon
                        icon={svgIcons.deleteIcon}
                        mobileSize={{ width: 24, height: 24 }}
                        webSize={{ width: 24, height: 24 }}
                      />
                    </View>
                  </Pressable>
                }
              </TableCell>
            ),
          },
        ]}
        rows={rows}
      />
      <Modal
        showModal={!!issueToDelete}
        title={t('admin:helpdesk_issues_list:sure_to_delete')}
        callback={() => {
          deleteHelpdeskIssue(issueToDelete);
          setIssueToDelete(null);
        }}
        closeModal={() => {
          setIssueToDelete(null);
        }}
        yesText={t('admin:helpdesk_issues_list:yes_delete')}
        cancelText={t('admin:helpdesk_issues_list:no')}
      />
    </View>
  );
};
export default HelpdeskIssueTable;
